import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ImagePlus, Loader2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, discountOf, fileUrl, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useShop } from "@/context/ShopContext";
import NotifyButton from "@/components/NotifyButton";

export default function ProductCard({ product, index = 0, onChanged }) {
  const { add } = useCart();
  const { isAdmin } = useAuth();
  const { maintenance } = useShop() || {};
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [sel, setSel] = useState(null);
  const fileRef = useRef(null);
  const d = discountOf(product);
  const variants = product.variants || [];
  const selV = sel != null ? variants[sel] : null;
  const image = selV?.image || product.images?.[0] || null;
  const out = variants.length ? selV != null && selV.stock <= 0 : product.stock <= 0;
  const allOut = variants.length ? variants.every((v) => v.stock <= 0) : product.stock <= 0;

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      if (selV) {
        await api.post(`/admin/products/${product.id}/variants/${sel}/image`, fd);
        toast.success(`Photo du coloris ${selV.name} ajoutée`);
      } else {
        await api.post(`/admin/products/${product.id}/images`, fd);
        toast.success("Photo ajoutée");
      }
      onChanged?.();
    } catch (err) {
      toast.error("Échec de l'envoi de la photo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleAdd = () => {
    if (maintenance) {
      toast.error("La boutique est en maintenance — commandes désactivées");
      return;
    }
    if (variants.length && !selV) {
      toast.error("Choisis d'abord un coloris");
      return;
    }
    add(product, selV);
    toast.success(selV ? `${product.name} (${selV.name}) ajouté au panier` : `${product.name} ajouté au panier`);
  };

  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.07, ease: [0.22, 1, 0.36, 1] }}
      className="group"
    >
      <div
        onClick={() => navigate(`/produit/${product.id}`)}
        data-testid={`product-image-link-${product.id}`}
        className="relative aspect-[3/4] rounded-2xl border border-kay-line bg-white overflow-hidden transition-transform duration-500 group-hover:scale-[1.02] shadow-[0_10px_30px_rgba(232,160,191,0.10)] cursor-pointer"
      >
        {image ? (
          <img
            src={fileUrl(image)}
            alt={selV ? `${product.name} — ${selV.name}` : product.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-3 border border-dashed border-kay-rosedeep/60 rounded-xl flex flex-col items-center justify-center gap-3 bg-kay-rose/40">
            <ImagePlus className="text-kay-pink/70" size={28} strokeWidth={1.5} />
            <p className="font-display italic text-lg text-kay-ink/50">Photo à venir</p>
          </div>
        )}
        {d > 0 && !product.coming_soon && (
          <span
            data-testid={`discount-badge-${product.id}`}
            className="absolute top-3 left-3 bg-kay-pink text-white font-mono text-xs font-bold tracking-widest px-2.5 py-1 rounded-full"
          >
            -{d} %
          </span>
        )}
        {product.coming_soon && (
          <span className="absolute top-3 left-3 bg-kay-ink text-kay-rose font-mono text-xs font-bold tracking-widest px-2.5 py-1 rounded-full">
            COMING SOON
          </span>
        )}
        {isAdmin && (
          <button
            data-testid={`photo-edit-${product.id}`}
            onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
            disabled={uploading}
            className="absolute bottom-3 inset-x-3 bg-white/90 backdrop-blur border border-kay-line rounded-full py-2 text-xs font-semibold flex items-center justify-center gap-2 hover:bg-kay-ink hover:text-white transition-colors"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
            {selV ? `Photo : ${selV.name}` : product.images?.length ? "Remplacer la photo" : "Ajouter une photo"}
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`photo-input-${product.id}`} />
      </div>

      <div className="pt-4 px-1">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-kay-pink">{product.brand}</p>
        <Link to={`/produit/${product.id}`} data-testid={`product-link-${product.id}`}>
          <h3 className="font-display text-xl sm:text-2xl leading-tight mt-1 hover:text-kay-pink transition-colors">{product.name}</h3>
        </Link>
        {product.price != null && (
          <div className="mt-2 flex items-baseline gap-2 flex-wrap">
            <span data-testid={`price-${product.id}`} className="font-display text-2xl font-semibold">{fmt(product.price)}</span>
            {product.reference_price > product.price && (
              <span data-testid={`ref-price-${product.id}`} className="ref-price font-mono text-xs text-kay-ink/40 line-through">
                {fmt(product.reference_price)}
              </span>
            )}
          </div>
        )}
        {product.reference_price > product.price && (
          <p data-testid={`savings-${product.id}`} className="savings-line font-mono text-[11px] text-kay-green font-semibold mt-1">
            Économise {fmt(product.reference_price - product.price)}
          </p>
        )}
        {variants.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`variants-${product.id}`}>
            {variants.map((v, i) => (
              <button
                key={v.name}
                data-testid={`variant-${product.id}-${i}`}
                onClick={() => setSel(i)}
                disabled={v.stock <= 0}
                className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-all ${
                  sel === i ? "bg-kay-ink text-white border-kay-ink" : "border-kay-line bg-white/80 hover:border-kay-ink"
                } ${v.stock <= 0 ? "opacity-35 line-through cursor-not-allowed" : ""}`}
              >
                {v.name}
              </button>
            ))}
          </div>
        )}
        {!product.coming_soon && (
          <>
            {variants.length > 0 && selV && selV.stock > 0 && selV.stock <= 3 && (
              <p className="text-[11px] text-kay-pink mt-1.5 italic">Plus que {selV.stock} en {selV.name}</p>
            )}
            {variants.length === 0 && product.stock > 0 && product.stock <= 3 && (
              <p className="text-[11px] text-kay-pink mt-1 italic">Plus que {product.stock} en stock</p>
            )}
          </>
        )}
        {product.coming_soon ? (
          <p className="mt-3 text-sm italic text-kay-ink/50 font-display text-lg">Bientôt disponible…</p>
        ) : allOut && !maintenance ? (
          <>
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-widest text-kay-ink/40" data-testid={`out-of-stock-${product.id}`}>Épuisé</p>
            <NotifyButton product={product} testid={`notify-${product.id}`} />
          </>
        ) : (
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={handleAdd}
            disabled={out || !!maintenance}
            className="add-btn mt-3 w-full rounded-full bg-kay-ink text-white py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:bg-kay-pink disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98]"
          >
            <ShoppingBag size={15} />
            {maintenance ? "Maintenance en cours" : out ? "Épuisé" : variants.length > 0 && !selV ? "Choisir un coloris" : "Ajouter au panier"}
          </button>
        )}
      </div>
    </motion.article>
  );
}
